import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ICatalog, ICatalogFilter } from 'src/app/features/catalogs/models/catalogs.models';
import { parseCatalogsFilterRequest, refillCatalogsFilter } from 'src/app/shared/utils/helpers';
import { environment } from 'src/environments/environment';
import { AppState } from 'src/app/core/app.state';
import { GetAgentsCatalogs } from 'src/app/features/agents/store/agents.actions';
import { CatalogsFilterComponent } from 'src/app/features/catalogs/components/catalogs-filter/catalogs-filter.component';
import { SetCatalogsFilter } from 'src/app/features/catalogs/store/catalogs.actions';
import { DATE_FORMAT } from 'src/app/config/global.config';

@Component({
  selector: 'app-agent-catalogs-list',
  templateUrl: './agent-catalogs-list.component.html',
  styleUrls: ['./agent-catalogs-list.component.scss']
})
export class AgentCatalogsListComponent implements OnInit {
  @Input() initialSelectedCatalogs: number[] = [];
  @Input() noDisclaimer: boolean = false; // This is used to restore the list to its original height when the disclaimer is closed (Agent Creation Page)
  @Output() selectedCatalogsChange = new EventEmitter<number[]>();
  
  catalogs$: Observable<ICatalog[]> = this.store.select(
    (state) => state.agents.catalogs
  );
  catalogs: ICatalog[] = [];
  filteredCatalogs: ICatalog[] = [];
  selectedCatalogs: number[] = [];
  searchInput = new FormControl('');

  filter: ICatalogFilter = refillCatalogsFilter();
  DATE_FORMAT = DATE_FORMAT;

  baseUrl = environment.BASE_URL_API;
  timestamp = new Date().getTime();
  failedImages = new Set<string>();

  activeSortMode: 'asc' | 'desc' | null = null;

  destroy$ = new Subject<void>();

  constructor(private store: Store<AppState>, private dialog: MatDialog) {}

  async ngOnInit(): Promise<void> {
    this.selectedCatalogs = this.initialSelectedCatalogs ? [...this.initialSelectedCatalogs] : [];

    this.catalogs$
      .pipe(
        filter((catalogs) => catalogs.length >= 0),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((catalogs: ICatalog[]) => {
        this.catalogs = [...catalogs].sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        this.filteredCatalogs = this.catalogs;
      });

    this.store
      .select((state) => state.catalogs.catalogsFilterState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filter) => {
        this.filter = filter.advanced;
        const filterRequest = parseCatalogsFilterRequest(filter.advanced);
        this.store.dispatch(
          GetAgentsCatalogs({
            page: { number: 0, size: 0 },
            filter: filterRequest,
          })
        );
      });
  }

  search(event: any) {
    const key = event.target.value.toLowerCase();

    if (key != '') {
      this.filteredCatalogs = this.catalogs.filter((catalogue) =>
        catalogue.name.toLowerCase().includes(key)
      );
    } else {
      this.filteredCatalogs = this.catalogs;
    }
  }

  openFilter() {
    const dialogRef = this.dialog.open(CatalogsFilterComponent, {
      minWidth: '650px',
      width: '80vw',
      height: '80vh',
    });

    dialogRef.afterClosed().subscribe((filter: ICatalogFilter) => {
      if (filter) {
        this.filter = filter;
        this.store.dispatch(
          SetCatalogsFilter({
            keyword: this.searchInput.value ?? '',
            advanced: filter,
          })
        );
      }
    });
  }

  clearFilter() {
    this.store.dispatch(
      SetCatalogsFilter({
        keyword: this.searchInput.value ?? '',
        advanced: refillCatalogsFilter(),
      })
    );
  }

  // Check if all catalogs in all companies are selected
  isAllCatalogsSelected(): boolean {
    const allCatalogueIds = this.filteredCatalogs.map(
      (catalogue: ICatalog) => catalogue.id
    );
    return allCatalogueIds.length
      ? allCatalogueIds.every((id: any) => this.selectedCatalogs.includes(id))
      : false;
  }

  // Check if at least one catalogue is selected but not all
  isPartialSelectedAll(): boolean {
    const allCatalogueIds = this.filteredCatalogs.map(
      (catalogue: ICatalog) => catalogue.id
    );
    const selectedCount = allCatalogueIds.filter((id: any) =>
      this.selectedCatalogs.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < allCatalogueIds.length;
  }

  toggleAllCatalogs(checked: boolean) {
    const allIds = this.filteredCatalogs.map((l: ICatalog) => l.id!);
    this.selectedCatalogs = checked ? [...allIds] : [];
    this.selectedCatalogsChange.emit(this.selectedCatalogs);
  }

  toggleCatalogue(catalogueId: number, checked: boolean) {
    if (checked) {
      this.selectedCatalogs.push(catalogueId);
    } else {
      this.selectedCatalogs = this.selectedCatalogs.filter(
        (id) => id !== catalogueId
      );
    }
    this.selectedCatalogsChange.emit(this.selectedCatalogs);
  }

  getImage(image: string): string {
    return this.baseUrl + image + '?thumbnail=1&timestamp=' + this.timestamp;
  }

  getFiltersCount(): number {
    return parseCatalogsFilterRequest(this.filter)?.filters?.length || 0;
  }

  onImageError(imageUrl: string): void {
    this.failedImages.add(imageUrl); // Mark this image as failed
  }

  sortByCreationDate(sortDirection: 'asc' | 'desc'): void {
    if (!this.filteredCatalogs || !Array.isArray(this.filteredCatalogs))
      return;

    this.filteredCatalogs = [...this.filteredCatalogs].sort((a, b) => {
      const dateA = new Date(a.create_date!).getTime();
      const dateB = new Date(b.create_date!).getTime();

      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });

    this.activeSortMode = sortDirection;
  }
}
